/**
 * PhoneNumpad — big-button numeric keypad for door-day phone lookup.
 *
 * Phase 8.14 (Aug 09 event prep). Two modes in one component:
 *   1. Inline: renders as a compact card below the search input.
 *   2. Fullscreen: same numpad but as a full-viewport overlay
 *      with jumbo keys — ideal for a tablet placed at the door.
 *
 * The component is purely presentational — it drives a parent-controlled
 * digits string via `onDigitsChange`. It never fetches or checks in. The
 * existing debounced search on the check-in page picks up the digits
 * exactly like it does for typed input.
 */
import { useEffect } from "react";
import { Delete, Maximize2, X as XIcon, Grid3x3 } from "lucide-react";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "C", "0", "back"];

function formatUsPhone(digits) {
  const d = (digits || "").replace(/\D/g, "").slice(0, 10);
  if (!d) return "";
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function KeyGrid({ onTap, size = "md", testIdPrefix }) {
  const keyClass = {
    sm: "h-12 text-lg",
    // Phase 8.17 — bumped default inline size for door-day tablet fingers.
    // Was h-16 text-2xl → h-20 sm:h-16 md:h-20 with larger text.
    md: "h-20 sm:h-16 md:h-20 text-3xl sm:text-2xl md:text-3xl",
    lg: "h-24 md:h-28 text-4xl md:text-5xl",
  }[size];
  return (
    <div
      className={`grid grid-cols-3 gap-2 md:gap-3 ${size === "lg" ? "gap-4 md:gap-5" : ""}`}
      data-testid={`${testIdPrefix}-keypad`}
    >
      {KEYS.map((k) => (
        <button
          key={k}
          type="button"
          onClick={() => onTap(k)}
          data-testid={`${testIdPrefix}-key-${k === "back" ? "back" : k.toLowerCase()}`}
          className={`${keyClass} rounded-2xl font-semibold flex items-center justify-center bg-white border border-espresso/10 text-espresso hover:bg-linen active:scale-[0.96] active:bg-linen transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-espresso/30 shadow-sm`}
          aria-label={k === "back" ? "backspace" : k === "C" ? "clear" : k}
        >
          {k === "back" ? <Delete className={size === "lg" ? "w-8 h-8" : "w-7 h-7"} /> : k}
        </button>
      ))}
    </div>
  );
}

export default function PhoneNumpad({
  digits = "",
  onDigitsChange,
  fullscreen = false,
  onFullscreenChange,
  lang = "vi",
}) {
  const label = lang === "en" ? "Numpad" : "Bàn phím số";
  const clearLabel = lang === "en" ? "Clear" : "Xoá hết";
  const fullscreenOn = lang === "en" ? "Full screen" : "Toàn màn hình";
  const fullscreenOff = lang === "en" ? "Exit full screen" : "Thoát toàn màn hình";

  const tap = (k) => {
    if (k === "back") return onDigitsChange((digits || "").slice(0, -1));
    if (k === "C") return onDigitsChange("");
    if (digits.length >= 10) return;
    onDigitsChange((digits || "") + k);
  };

  // Phase 8.17 — auto-open fullscreen on landscape tablet (once per session).
  // Detects tablet-class viewports (768–1180px width, landscape) and opens the
  // fullscreen numpad on first mount so door staff get big keys immediately.
  // Persists a "prompted" flag in sessionStorage so it only fires once — the
  // user can close it and never see it again this tab session.
  useEffect(() => {
    if (fullscreen) return;
    try {
      if (window.sessionStorage.getItem("nailViet.numpadTabletPrompted") === "1") return;
      const w = window.innerWidth;
      const h = window.innerHeight;
      const isLandscapeTablet = w >= 768 && w <= 1180 && w > h;
      if (isLandscapeTablet) {
        window.sessionStorage.setItem("nailViet.numpadTabletPrompted", "1");
        onFullscreenChange?.(true);
      }
    } catch (_e) { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fullscreen mode: lock body scroll + esc to close.
  useEffect(() => {
    if (!fullscreen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onFullscreenChange?.(false);
      if (/^[0-9]$/.test(e.key)) tap(e.key);
      if (e.key === "Backspace") tap("back");
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullscreen, digits]);

  const displayText = digits ? formatUsPhone(digits) : "";
  const hint = lang === "en"
    ? "Enter the attendee's 10-digit phone"
    : "Nhập 10 số điện thoại của khách";

  if (fullscreen) {
    return (
      <div
        data-testid="numpad-fullscreen"
        className="fixed inset-0 z-[60] bg-espresso/95 backdrop-blur-md text-linen flex flex-col"
      >
        <header className="flex items-center justify-between px-6 md:px-10 py-4 border-b border-linen/10">
          <div>
            <p className="text-[11px] uppercase tracking-[0.28em] text-linen/60 font-semibold">
              {label}
            </p>
            <p className="text-linen/80 text-sm mt-1">{hint}</p>
          </div>
          <button
            type="button"
            onClick={() => onFullscreenChange?.(false)}
            data-testid="numpad-fullscreen-close"
            className="rounded-full bg-white/10 hover:bg-white/20 text-linen p-3 transition-colors"
            aria-label={fullscreenOff}
          >
            <XIcon className="w-6 h-6" />
          </button>
        </header>
        <div className="flex-1 flex flex-col items-center justify-center px-6 py-8 gap-8">
          <div
            data-testid="numpad-display-fullscreen"
            className={`font-serif tabular-nums text-5xl md:text-7xl tracking-widest text-white min-h-[80px] flex items-center ${digits ? "" : "text-linen/40"}`}
          >
            {displayText || "(___) ___-____"}
          </div>
          <div className="w-full max-w-[560px]">
            <KeyGrid onTap={tap} size="lg" testIdPrefix="numpad-fullscreen" />
          </div>
          <p className="text-linen/60 text-xs md:text-sm">
            {lang === "en"
              ? "Search runs automatically when you finish typing."
              : "Tìm kiếm tự động chạy khi bạn nhập xong."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="numpad-inline"
      className="mt-4 rounded-2xl border border-espresso/10 bg-white/70 backdrop-blur-md p-4"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-espresso-soft">
          <Grid3x3 className="w-4 h-4" />
          <span className="text-[11px] uppercase tracking-[0.24em] font-semibold">{label}</span>
        </div>
        <div className="flex items-center gap-2">
          {digits && (
            <button
              type="button"
              onClick={() => onDigitsChange("")}
              data-testid="numpad-inline-clear"
              className="text-xs font-medium text-espresso-muted hover:text-espresso underline decoration-espresso/20 hover:decoration-espresso"
            >
              {clearLabel}
            </button>
          )}
          <button
            type="button"
            onClick={() => onFullscreenChange?.(true)}
            data-testid="numpad-fullscreen-toggle"
            className="inline-flex items-center gap-1.5 rounded-full bg-espresso text-linen px-3 py-1.5 text-xs font-semibold hover:bg-espresso/90 active:scale-[0.97] transition"
            aria-label={fullscreenOn}
          >
            <Maximize2 className="w-3.5 h-3.5" />
            {fullscreenOn}
          </button>
        </div>
      </div>
      <KeyGrid onTap={tap} size="md" testIdPrefix="numpad-inline" />
    </div>
  );
}
